import { useState } from 'react';
import { useLeadReplyWithMaterials } from '../hooks/useLeadReplyWithMaterials';
import MaterialsSelectionPanel from '@/features/tickets/components/MaterialsSelectionPanel';
import type { CreateReplyWithMaterialsRequest } from '@/types';
import {
  CheckCircleIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface LeadReplyComposerProps {
  leadId: string;
  /** Provide when used inside a prospect detail view — enables prospect-scoped query invalidation. */
  prospectId?: string;
}

interface ValidationError {
  materialId: string;
  reason: string;
}

/**
 * Lead reply composer with marketing materials attachment support
 * Handles material selection, submission, error handling, and session storage persistence
 */
export default function LeadReplyComposer({ leadId, prospectId }: LeadReplyComposerProps) {
  const [bodyText, setBodyText] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [showMaterialsPanel, setShowMaterialsPanel] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  
  // Session storage key for selection persistence
  const storageKey = `lead-reply-materials-${leadId}`;
  
  // Load initial selection from sessionStorage
  const loadSelection = (): string[] => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };
  
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<string[]>(loadSelection);

  // Save selection to sessionStorage whenever it changes
  const updateSelection = (ids: string[]) => {
    setSelectedMaterialIds(ids);
    try {
      if (ids.length > 0) {
        sessionStorage.setItem(storageKey, JSON.stringify(ids));
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch (error) {
      console.error('Failed to save selection to sessionStorage:', error);
    }
  };

  const clearSelection = () => {
    setSelectedMaterialIds([]);
    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Failed to clear selection from sessionStorage:', error);
    }
  };

  const handleToggleMaterial = (materialId: string) => {
    if (selectedMaterialIds.includes(materialId)) {
      updateSelection(selectedMaterialIds.filter(id => id !== materialId));
    } else {
      if (selectedMaterialIds.length >= 10) {
        alert('Maximum 10 materials can be attached per reply.');
        return;
      }
      updateSelection([...selectedMaterialIds, materialId]);
    }
  };

  const handleSuccess = () => {
    // Clear form and selection on success
    setBodyText('');
    setBodyHtml('');
    clearSelection();
    setShowMaterialsPanel(false);
    setValidationErrors([]);
    
    // Show success toast
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  const handleError = (error: Error) => {
    // Handle 422 UnprocessableEntity material validation errors
    try {
      const errorData = JSON.parse(error.message);
      if (errorData.errors && Array.isArray(errorData.errors)) {
        setValidationErrors(errorData.errors);
      } else {
        alert(`Failed to send reply: ${error.message}`);
      }
    } catch {
      // Not a structured error, show generic message
      alert(`Failed to send reply: ${error.message}`);
    }
  };

  const replyMutation = useLeadReplyWithMaterials({
    leadId,
    prospectId,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const handleSubmit = () => {
    if (!bodyText.trim()) {
      alert('Please enter a reply message.');
      return;
    }

    const request: CreateReplyWithMaterialsRequest = {
      bodyText,
      bodyHtml: bodyHtml || undefined,
      materialIds: selectedMaterialIds,
    };

    setValidationErrors([]);
    replyMutation.mutate(request);
  };

  return (
    <div className="rounded-xl border border-brand-border bg-white p-3 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-brand-text-primary">Reply</h3>
        {selectedMaterialIds.length > 0 && (
          <span
            className="rounded-full bg-brand-accent px-2.5 py-1 text-xs font-medium text-brand-text-inverse"
            aria-label={`${selectedMaterialIds.length} materials selected`}
          >
            {selectedMaterialIds.length} attached
          </span>
        )}
      </div>
      
      {/* Reply text area */}
      <textarea
        value={bodyText}
        onChange={e => setBodyText(e.target.value)}
        rows={4}
        placeholder="Write your reply…"
        className="w-full resize-none rounded-xl border border-brand-border bg-brand-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
        disabled={replyMutation.isPending}
        aria-label="Reply message"
        aria-required="true"
      />

      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowMaterialsPanel(!showMaterialsPanel)}
            className="flex items-center gap-2 rounded-md border border-brand-border bg-brand-surface-card px-3 py-1.5 text-sm transition-colors hover:bg-brand-surface"
            type="button"
            disabled={replyMutation.isPending}
            aria-label={showMaterialsPanel ? "Hide materials panel" : "Show materials panel"}
            aria-expanded={showMaterialsPanel}
            title={showMaterialsPanel ? 'Hide materials panel' : 'Add marketing materials'}
          >
            <PaperClipIcon className="h-4 w-4" title={showMaterialsPanel ? 'Hide materials panel' : 'Add marketing materials'} />
            <span>{showMaterialsPanel ? 'Hide Materials' : 'Attach Materials'}</span>
          </button>
          
          {selectedMaterialIds.length > 0 && (
            <button
              onClick={clearSelection}
              className="flex items-center gap-1 rounded-md border border-brand-border px-2 py-1 text-xs text-brand-text-secondary hover:bg-brand-surface"
              type="button"
              disabled={replyMutation.isPending}
              aria-label="Clear material selection"
              title="Clear selected materials"
            >
              <XMarkIcon className="h-3.5 w-3.5" title="Clear selected materials" />
              Clear
            </button>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!bodyText.trim() || replyMutation.isPending}
          className="inline-flex items-center gap-2 rounded-full bg-brand-accent px-4 py-2 text-sm text-brand-text-inverse transition-colors hover:bg-brand-accent/90 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={replyMutation.isPending ? "Sending reply" : "Send reply with attachments"}
          title={replyMutation.isPending ? 'Sending reply' : 'Send reply'}
        >
          {replyMutation.isPending ? (
            <span className="flex items-center gap-2">
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-brand-text-inverse border-t-transparent" />
              Sending...
            </span>
          ) : (
            <>
              <PaperAirplaneIcon className="h-4 w-4" title="Send reply" />
              Send
            </>
          )}
        </button>
      </div>

      {/* Materials selection panel */}
      {showMaterialsPanel && (
        <div className="mt-4">
          <MaterialsSelectionPanel
            selectedMaterialIds={selectedMaterialIds}
            onToggleMaterial={handleToggleMaterial}
          />
        </div>
      )}

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
          <h4 className="text-sm font-semibold text-red-900 mb-2">
            Some materials cannot be attached:
          </h4>
          <ul className="text-sm text-red-700 space-y-1">
            {validationErrors.map((err, idx) => (
              <li key={idx}>
                <strong>Material {err.materialId.slice(0, 8)}...</strong>: {err.reason}
              </li>
            ))}
          </ul>
          <p className="text-xs text-red-600 mt-2">
            Please remove these materials from your selection and try again.
          </p>
        </div>
      )}

      {/* Success Toast */}
      {showSuccessToast && (
        <div 
          className="fixed bottom-4 right-4 bg-brand-accent text-brand-text-inverse px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-slide-up z-50"
          role="alert"
          aria-live="polite"
        >
          <CheckCircleIcon className="h-5 w-5" title="Success" />
          <span className="font-medium">Reply sent successfully!</span>
        </div>
      )}
    </div>
  );
}
